// The faces of a deck's cards (docs/areas/learn.md): what a card says, and the tiles under it. Each is
// one idea, readable in under ten seconds, and none of them scrolls. DeckView owns what happens next.
import { useMemo, type ReactNode } from 'react'
import { deckTileAfter, deckTiles } from '@/lib/colour'
import type { DeckCard, ExerciseCard } from '@/lib/learnDeck'
import type { Outcome } from '@/lib/learnSession'

/** A small deterministic shuffle: a card's tiles keep their places while it is shown, and differ from card to card. */
export const orderOf = (n: number, seed: string): number[] => {
  let h = 2166136261
  for (const ch of seed) h = Math.imul(h ^ ch.charCodeAt(0), 16777619)
  const idx = [...Array(n).keys()]
  for (let i = n - 1; i > 0; i--) {
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0
    const j = h % (i + 1)
    ;[idx[i], idx[j]] = [idx[j]!, idx[i]!]
  }
  return idx
}

/** A learn card's text is "heading\nbody". */
export const splitLearn = (prompt: string): { heading: string; body: string } => {
  const i = prompt.indexOf('\n')
  return i < 0 ? { heading: '', body: prompt } : { heading: prompt.slice(0, i), body: prompt.slice(i + 1) }
}

const Shape = ({ kind }: { kind: (typeof deckTiles)[number]['shape'] }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden className="shrink-0" fill="currentColor">
    {kind === 'triangle' && <path d="M9 2 16 15H2z" />}
    {kind === 'diamond' && <path d="M9 1 17 9 9 17 1 9z" />}
    {kind === 'circle' && <circle cx="9" cy="9" r="7" />}
    {kind === 'square' && <rect x="2.5" y="2.5" width="13" height="13" rx="1.5" />}
  </svg>
)

export const Code = ({ text }: { text: string }) => (
  <pre
    className="mt-3 rounded-[var(--radius-card-inner)] bg-sunken border border-line px-3 py-2.5 font-mono text-[12px] leading-[1.5] text-fg-2 overflow-hidden whitespace-pre"
    data-testid="deck-code"
  >
    {text}
  </pre>
)

/** The middle of the card: its words, and its code if it has some. */
export const CardBody = ({ card, flipped }: { card: DeckCard; flipped: boolean }): ReactNode => {
  if (card.format === 'lesson')
    return (
      <div data-testid="deck-lesson">
        <div className="text-xs text-faint mb-2">Lesson</div>
        <div className="text-[28px] leading-9 font-semibold mb-3">{card.lesson.spec.title}</div>
        {card.lesson.spec.estimate_minutes && (
          <p className="text-[17px] text-fg-2">About {card.lesson.spec.estimate_minutes} minutes.</p>
        )}
      </div>
    )
  if (card.format === 'concept')
    return flipped ? (
      <div data-testid="deck-back">
        <div className="text-xs text-faint mb-2">When not to</div>
        <p className="text-[19px] leading-7 line-clamp-[8]">
          {card.concept.spec.when_not_to ||
            card.concept.spec.when_to_use ||
            card.concept.spec.my_notes ||
            'Nothing written here yet.'}
        </p>
      </div>
    ) : (
      <div data-testid="deck-front">
        {card.concept.spec.category && (
          <div className="text-xs text-faint mb-2">{card.concept.spec.category}</div>
        )}
        <div className="text-[30px] leading-9 font-semibold mb-3">{card.concept.spec.name}</div>
        <p className="text-[19px] leading-7 text-fg-2 line-clamp-[6]">{card.concept.spec.one_liner}</p>
      </div>
    )
  const x = card.exercise.spec
  const who = card.concepts.map((c) => c.spec.name).join(' + ')
  if (card.format === 'learn') {
    const { heading, body } = splitLearn(x.prompt)
    const back = card.concepts.length === 1 ? card.concepts[0]!.spec.when_not_to : ''
    return flipped ? (
      <div data-testid="deck-back">
        <div className="text-xs text-faint mb-2">{who}, when not to</div>
        <p className="text-[19px] leading-7 line-clamp-[8]">{back || 'Nothing written here yet.'}</p>
      </div>
    ) : (
      <div data-testid="deck-front" data-combo={card.concepts.length > 1 || undefined}>
        <div className="text-xs text-faint mb-2">{heading ? `${who}, ${heading.toLowerCase()}` : who}</div>
        <p className="text-[21px] leading-8 text-fg line-clamp-[8]">{body}</p>
      </div>
    )
  }
  return (
    <div data-testid={`deck-${card.format}`} data-combo={card.concepts.length > 1 || undefined}>
      {card.concepts.length > 1 && <div className="text-xs text-faint mb-2">Together</div>}
      {x.code_excerpt && card.format !== 'judge' && (
        <div className="text-[17px] leading-6 font-medium">{x.prompt}</div>
      )}
      {x.code_excerpt && <Code text={x.code_excerpt} />}
      {x.code_excerpt && card.format === 'judge' && (
        <div className="mt-3 text-[19px] leading-7 font-medium">{x.prompt}</div>
      )}
      {!x.code_excerpt && <div className="text-[21px] leading-8 font-medium">{x.prompt}</div>}
    </div>
  )
}

/** The tiles of a quick pick or a spot card (a judge has its own two buttons). */
export const Tiles = ({
  card,
  seed,
  picked,
  outcome,
  disabled,
  onTile,
}: {
  card: ExerciseCard
  seed: string
  picked: number[]
  outcome: Outcome | null
  disabled: boolean
  onTile: (i: number) => void
}) => {
  const choices = card.exercise.spec.choices ?? []
  const order = useMemo(() => orderOf(choices.length, `${card.id}:${seed}`), [choices.length, card.id, seed])
  return (
    <div className="grid grid-cols-2 gap-2.5" data-testid="deck-tiles">
      {order.map((ci, pos) => {
        const t = deckTiles[pos % deckTiles.length]!
        const isAnswer = outcome?.correct?.includes(ci)
        return (
          <button
            key={ci}
            type="button"
            disabled={disabled}
            onClick={() => onTile(ci)}
            data-testid="deck-tile"
            data-answer={isAnswer || undefined}
            aria-pressed={picked.includes(ci)}
            className={`flex items-center gap-2.5 min-h-[4.5rem] px-3 rounded-[var(--radius-card-inner)] border text-left text-[15px] leading-5 font-medium active:scale-[0.98] transition ${t.look} ${
              outcome
                ? isAnswer
                  ? deckTileAfter.answer
                  : picked.includes(ci)
                    ? deckTileAfter.picked
                    : deckTileAfter.missed
                : picked.includes(ci)
                  ? deckTileAfter.picked
                  : ''
            }`}
          >
            <Shape kind={t.shape} />
            <span>{choices[ci]!.label}</span>
          </button>
        )
      })}
    </div>
  )
}
